/**
 * Opens the consent page the connectors service returns.
 *
 * Safari grants `window.open` only during the click, and the consent link does
 * not arrive until the connect request returns. So a tab opens empty on click
 * and is pointed at the link once it lands.
 */

/** Consent is an authorization step, so anything but HTTPS is refused. */
export function isSafeConsentUrl(url: string): boolean {
  try {
    // The base stops relative input from throwing. The prefix check is what
    // rejects it, since a relative path inherits this page's https scheme.
    return new URL(url, window.location.origin).protocol === 'https:' && url.startsWith('https://')
  } catch {
    return false
  }
}

/**
 * Opens a blank tab to spend the click's activation.
 *
 * Clearing `opener` blocks the consent page from navigating this one, while
 * this page keeps the handle it needs to send the tab onward.
 */
export function openBlankTab(): Window | null {
  const tab = window.open('', '_blank')
  if (tab) {
    tab.opener = null
  }
  return tab
}

/** Sends a tab opened on click to the consent page. False if the link is unusable or no tab opened. */
export function navigateToConsent(tab: Window | null, url: string): boolean {
  if (!isSafeConsentUrl(url)) {
    tab?.close()
    return false
  }

  if (tab && !tab.closed) {
    // Replace so the back button does not land on the blank page.
    tab.location.replace(url)
    return true
  }

  // No tab survived the click, so ask for one now. Blocked unless the browser
  // still counts the click as recent.
  return window.open(url, '_blank', 'noopener,noreferrer') !== null
}
